import React, { useState } from 'react';
import RichBodyEditor from '../RichBodyEditor';
import TemplateLibrary from '../TemplateLibrary';

// One shared subject/body template per round — the suggested-people block
// is what actually varies per recipient (rendered separately, per tab, in
// SuggestionPreviewTabs), so there's no need for a per-recipient
// hand-written draft on top of that.
//
// Field picker never offers email/phone as options at all — this is UI
// reinforcement on top of the backend's own hard enforcement
// (suggestionRenderer.js), not a substitute for it.
const ComposerPanel = ({
  fields, includedFields, setIncludedFields,
  sharedTemplate, setSharedTemplate,
  includeSignature = false, setIncludeSignature = () => {},
  sendAsMe = false, setSendAsMe = () => {},
  communityId, authedFetch,
}) => {
  const [instructions, setInstructions] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  const toggleBaseField = (key) => {
    setIncludedFields((prev) => {
      const has = prev.base.includes(key);
      return { ...prev, base: has ? prev.base.filter((f) => f !== key) : [...prev.base, key] };
    });
  };

  const toggleCommunityField = (name) => {
    setIncludedFields((prev) => {
      const has = prev.community.includes(name);
      return { ...prev, community: has ? prev.community.filter((f) => f !== name) : [...prev.community, name] };
    });
  };

  const handleAiAssist = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const action = sharedTemplate.subject || sharedTemplate.body ? 'tweak' : 'draft';
      const result = await authedFetch('/admin/suggestions/ai-assist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          communityId,
          instructions,
          currentSubject: sharedTemplate.subject,
          currentBody: sharedTemplate.body,
        }),
      });
      setSharedTemplate({ subject: result.subject, body: result.htmlBody });
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="as-email-compose">
      <div className="as-suggestion-field-picker">
        <span className="as-card-meta">Include in suggested-person cards:</span>
        <div className="as-suggestion-field-checks">
          {fields.baseFields.map((f) => (
            <label key={f.key} className="as-suggestion-checkbox-label">
              <input type="checkbox" checked={includedFields.base.includes(f.key)} onChange={() => toggleBaseField(f.key)} />
              {f.label}
            </label>
          ))}
          {fields.communityFields.map((name) => (
            <label key={name} className="as-suggestion-checkbox-label">
              <input type="checkbox" checked={includedFields.community.includes(name)} onChange={() => toggleCommunityField(name)} />
              {name}
            </label>
          ))}
        </div>
      </div>

      <TemplateLibrary
        authedFetch={authedFetch}
        subject={sharedTemplate.subject}
        body={sharedTemplate.body}
        onLoad={setSharedTemplate}
      />

      <div className="as-email-field">
        <label>Subject</label>
        <input type="text" value={sharedTemplate.subject || ''} onChange={(e) => setSharedTemplate((prev) => ({ ...prev, subject: e.target.value }))} placeholder="{{firstName}}, meet someone new in {{communityName}}!" />
      </div>

      <div className="as-activity-toolbar" style={{ marginTop: '0.75rem' }}>
        <label className="as-suggestion-checkbox-label">
          <input type="checkbox" checked={sendAsMe} onChange={(e) => setSendAsMe(e.target.checked)} />
          Send as me (From: &quot;James at {'{community}'}&quot;)
        </label>
        <label className="as-suggestion-checkbox-label">
          <input type="checkbox" checked={includeSignature} onChange={(e) => setIncludeSignature(e.target.checked)} />
          Add my signature
        </label>
      </div>

      <div className="as-email-field">
        <label>Body</label>
        <div className="as-card-meta" style={{ marginBottom: '0.5rem', lineHeight: 1.65 }}>
          <div>Placeholders, replaced automatically:</div>
          <ul style={{ margin: '0.3rem 0 0.5rem', paddingLeft: '1.2rem' }}>
            <li><code>{'{{firstName}}'}</code>, <code>{'{{communityName}}'}</code>, <code>{'{{suggestedNames}}'}</code></li>
            <li><code>{'{{suggestedPeopleBlock}}'}</code> — cards, "Connect" button per person</li>
            <li><code>{'{{viewRespondSuggestionList}}'}</code> — same cards, button says "View and Respond" and links to the /respond page instead of one-click connecting</li>
            <li><code>{'{{suggestedPeopleBlock_<Label>}}'}</code> — same cards linking to /respond, with your own button wording (e.g. <code>{'{{suggestedPeopleBlock_Respond}}'}</code>, <code>{'{{suggestedPeopleBlock_Connect Now}}'}</code>) — everything after the underscore is used verbatim as the label</li>
            <li><code>{'{{suggestedPeopleTeaser}}'}</code> — LinkedIn/Freecycle-digest style: overlapping avatar circles, "X are keen to connect in Y", one "See who it is →" button, no bios or per-person buttons — to entice a click-through rather than let them decide from the inbox</li>
            <li><code>{'{{suggestedPeopleTeaser_<Label>}}'}</code> — same teaser, your own button wording instead of "See who it is →"</li>
            <li><code>{'{{suggestedPeopleMightConnect}}'}</code> — the same avatar teaser, worded "You might be interested in connecting with X, Y and Z in {'<community>'}", with a "Take a look →" button</li>
            <li><code>{'{{suggestedPeopleMightConnect_<Label>}}'}</code> — same, your own button wording</li>
            <li><code>{'{{respondButton}}'}</code> — "View and Respond" button</li>
            <li><code>{'{{connectPromptButton}}'}</code> — "Click here to let us know who you'd like to connect with" (same link, different wording)</li>
          </ul>
          To hyperlink your own text instead of a pre-styled button, highlight it and click "🔗 Link to Respond" above (or in Raw HTML, use <code>{'{{respondUrl}}'}</code> as the href).
        </div>
        <RichBodyEditor
          value={sharedTemplate.body}
          onChange={(html) => setSharedTemplate((prev) => ({ ...prev, body: html }))}
          placeholder="Hi {{firstName}}, ... {{suggestedPeopleBlock}}"
          showRespondLinkButton
        />
      </div>

      <div className="as-suggestion-ai-assist">
        {aiError && <div className="as-error">{aiError}</div>}
        <input
          type="text"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Instructions for the AI (e.g. warm and casual, mention golf)"
        />
        <button type="button" className="as-filter-tab" onClick={handleAiAssist} disabled={aiLoading || !communityId}>
          {aiLoading ? 'Thinking…' : '✨ AI Draft / Tweak'}
        </button>
      </div>
    </div>
  );
};

export default ComposerPanel;
